import type { FC } from "react";
import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

import StarIcon from "assets/icons/filled/award-star.svg?react";
import CardIcon from "assets/icons/filled/card.svg?react";
import HourglassIcon from "assets/icons/filled/hourglass.svg?react";
import LoyaltyIcon from "assets/icons/filled/loyalty.svg?react";
import MailboxIcon from "assets/icons/filled/mailbox.svg?react";
import SpeedometerIcon from "assets/icons/filled/speedometer.svg?react";
import { getPaths } from "core/getPaths";
import Switcher from "shared/Components/Switcher";

import useResize from "shared/hooks/useResize";

const { profile: profileRoutes } = getPaths();

const ProfilePayout: FC = () => {
	const { t } = useTranslation();

	const { pathname } = useLocation();

	const { isMobile } = useResize();

	const url = useMemo(() => pathname.split("/")[pathname.split("/").length - 1], [pathname]);

	const tabsOpts = useMemo(
		() => [
			{
				activeIcon: <SpeedometerIcon className="svg-paths:fill-brown-400" />,
				icon: <SpeedometerIcon className="svg-paths:fill-yellow-350" />,
				label: t("dashboard"),
				value: "dashboard",
				link: profileRoutes.dashboard.getPath()
			},
			{
				activeIcon: <StarIcon className="svg-paths:fill-brown-400" />,
				icon: <StarIcon className="svg-paths:fill-yellow-350" />,
				label: t("bonuses"),
				value: "bonuses",
				link: profileRoutes.bonuses.getPath()
			},
			{
				activeIcon: <LoyaltyIcon className="svg-paths:fill-brown-400" />,
				icon: <LoyaltyIcon className="svg-paths:fill-yellow-350" />,
				label: t("loyalty"),
				value: "loyalty",
				link: profileRoutes.loyalty.getPath()
			},
			{
				activeIcon: <CardIcon className="svg-paths:fill-brown-400" />,
				icon: <CardIcon className="svg-paths:fill-yellow-350" />,
				label: t("transactions"),
				value: "transactions",
				link: profileRoutes.transactions.getPath()
			},
			{
				activeIcon: <HourglassIcon className="svg-paths:fill-brown-400" />,
				icon: <HourglassIcon className="svg-paths:fill-yellow-350" />,
				label: t("history"),
				value: "history",
				link: profileRoutes.history.getPath()
			},
			{
				activeIcon: <MailboxIcon className="svg-paths:fill-brown-400" />,
				icon: <MailboxIcon className="svg-paths:fill-yellow-350" />,
				label: t("general"),
				value: "general",
				link: profileRoutes.general.getPath()
			}
		],
		[t]
	);

	const activeSection = useMemo(() => tabsOpts.find(x => x.value === url) ?? null, [tabsOpts, url]);

	return (
		<div className="w-full rounded-2xl overflow-hidden bg-red-dark p-2 sm:pt-6 sm:pb-8 sm:px-[64px] max-sm:-mt-2">
			<div className="w-full mb-5 sm:mb-7">
				<Switcher value={url} options={tabsOpts} bordered activeLabel onlyIcons={isMobile} />
			</div>
			{isMobile && activeSection && (
				<div className="flex items-center gap-2 mb-1.5">
					{activeSection.icon}
					<p className="font-mic text-2xl text-yellow-350 uppercase">{activeSection.label}</p>
				</div>
			)}
			<Outlet />
		</div>
	);
};

export default ProfilePayout;
